import type {
  BaccaratRules,
  BaccaratSpot,
  Card,
  CrapsBetId,
  CrapsRoll,
  CrapsRules,
  GameType,
  HoldemOptions,
  KenoRules,
  Phase,
  PrivateHand,
  PrivateHandView,
  PublicBaccarat,
  PublicBaccaratResult,
  PublicCard,
  PublicCraps,
  PublicCrapsBet,
  PublicDeadline,
  PublicHand,
  PublicKenoTicket,
  PublicPlayer,
  PublicRouletteBet,
  PublicSeat,
  PublicRoulette,
  RoomState,
  RouletteBetId,
  RouletteBetNum,
  RouletteBetSpec,
  RoulettePocket,
  RouletteRules,
  SideBetId,
  ThreecardOutcome,
  ThreecardRules,
} from '@tv-casino/shared';
import {
  CRAPS_BETS,
  ROULETTE_BETS,
  crapsCanPress,
  describeKenoTicket,
  gameLabel,
  isHardRoll,
  pocketColor,
  profileInitials,
  rollTotal,
  wheelOrder,
} from '@tv-casino/shared';
import type { CrapsSpotMark } from '../components/crapsFelt.js';
import { gamePollModel, type GamePollVM } from '../components/gamePollModel.js';
import { kenoTicketColorIndex, ticketResultLabel } from '../components/kenoBoardModel.js';
import {
  crapsMarks,
  crapsRollCall,
  crapsSeatStaked,
  crapsStacks,
  type CrapsStackMap,
} from '../components/crapsFelt.js';
import {
  rouletteStacks,
  rouletteWinMarks,
  type RouletteSpotMark,
  type RouletteStackMap,
} from '../components/rouletteFelt.js';
import { formatSignedChips } from '../lib/format.js';
import { baccaratSideBetNotice } from '../lib/payouts.js';

/**
 * The pure view-model for the phone route. Everything Play.tsx shows or gates
 * on is derived here from (state, private hand, player id) — no React, no
 * socket, no storage — so UI assertions become object comparisons.
 */

export interface PlayHandRow {
  cards: Card[];
  label: string;
  active: boolean;
  bet: number;
}

export interface PlaySideBetRow {
  key: string;
  win: boolean;
  label: string;
}

export interface PlayResultRow {
  key: number;
  className: string | null;
  label: string;
}

export interface PlayPayoutLine {
  win: boolean;
  loss: boolean;
  label: string;
}

export interface PlayHoldemActionsVM {
  options: HoldemOptions;
  /** Chips still behind, the ceiling the tray can put in. */
  chips: number;
  potTotal: number;
  currentBet: number;
  bigBlind: number;
}

export interface PlayBetControlsVM {
  sideBets: SideBetId[];
  sideBetMax: number;
  minBet: number;
}

export interface PlayTableRow {
  index: number;
  name: string | null;
  nameLine: string;
  mine: boolean;
  acting: boolean;
  chipsLine: string;
  /** Hold'em: the cards the table shows for this seat, private cards included for me. */
  cards: (Card | undefined)[] | null;
  handLabel: string | null;
  fallback: string | null;
  committed: number;
  /** Blackjack: every hand on the seat, public or (for my seats) private. */
  hands: {
    cards: (Card | undefined)[];
    valueLabel: string;
    bet: number;
    handTag: string | null;
    result: string | null;
  }[];
}

export interface PlayTableViewVM {
  holdem: boolean;
  streetLabel: string | null;
  boardCards: (Card | undefined)[];
  potLine: string | null;
  dealerLabel: string;
  dealerCards: (Card | undefined)[];
  dealerValueLine: string | null;
  baccarat: {
    playerCards: (Card | undefined)[];
    bankerCards: (Card | undefined)[];
    playerTotal: number | null;
    bankerTotal: number | null;
    playerLabel: 'Player';
    bankerLabel: 'Banker';
  } | null;
  /** The craps layout, drawn as a table rather than described as cards. */
  craps: {
    rules: CrapsRules;
    point: number | null;
    stacks: CrapsStackMap;
    marks: Partial<Record<CrapsBetId, CrapsSpotMark>>;
    /** The stickman's call on the last roll. */
    callLine: string | null;
    shooterLine: string | null;
  } | null;
  rows: PlayTableRow[];
  nobodySeated: boolean;
}

/** The phone's three screens. `casino` only exists on a casino table. */
export type PlayView = 'controls' | 'table' | 'casino';

export interface PlayNavTab {
  id: PlayView;
  label: string;
  active: boolean;
}

export interface PlayVM {
  phase: Phase;
  gameBadge: string | null;
  amHost: boolean;
  /**
   * Identity chrome the phone and web Play headers share: room line, name
   * hero, host role, and the seat-mark initials. Null/empty parts stay off
   * the glass until the seat is known.
   */
  roomLabel: string | null;
  playerName: string | null;
  /** "You run the table" when this phone deals; renderers uppercase it. */
  roleLine: string | null;
  /** Initials for the seat avatar disc beside the seat pill. */
  seatInitials: string;
  /** Socket pill: "Seat N" or "Reconnecting…". */
  seatPill: string;
  online: boolean;

  mySeats: number[];
  mySeatIndex: number;
  mySeat: PublicSeat | null;
  myPlayer: PublicPlayer | null;
  seatTabs: { index: number; label: string; active: boolean }[];
  /**
   * The top-level screen switcher: my controls, the whole felt, and — on a
   * casino table — the lobby. One strip instead of two loose toggles, so
   * there is always one visible way back to where you were.
   */
  navTabs: PlayNavTab[];
  /**
   * Which of those screens is up. The route holds the state; the model hands
   * it back resolved so a renderer draws the view rather than inferring it
   * from which tab happens to be lit.
   */
  view: PlayView;
  canReleaseSeat: boolean;

  chipsAvailable: number;
  hostName: string | null;
  deadline: { endsAt: number; label: string } | null;

  banner: string | null;
  /**
   * True when the host has been gone long enough that the server will hand
   * the table to whoever asks: the renderer pairs this with the banner as a
   * "Take over the table" button.
   */
  canClaimHost: boolean;
  notice: string | null;

  /**
   * The open vote on the next game, or null when nobody is polling. Every
   * player sees it — the host opened it, but they vote like anyone else.
   */
  gamePoll: GamePollVM | null;

  ready: boolean;
  canDeal: boolean;
  dealLabel: string | null;
  canTakeAnother: boolean;
  openSeats: number[];
  showHostPanel: boolean;
  showReadyToggle: boolean;
  readyLabel: string | null;

  // Blackjack
  isHoldem: boolean;
  canBet: boolean;
  betControls: PlayBetControlsVM | null;
  canClearBet: boolean;
  clearBetLabel: string | null;
  canSitOut: boolean;
  canRebuy: boolean;
  rebuyLabel: string | null;
  isMyTurn: boolean;
  canHit: boolean;
  canStand: boolean;
  canDouble: boolean;
  canSplit: boolean;
  canSurrender: boolean;
  myHands: PlayHandRow[];
  handPlaceholder: string;
  sittingOut: boolean;
  waitingForOtherPlayer: boolean;
  /** Blackjack: dealer cards on My controls (hole as `undefined` until reveal). */
  dealerCards: (Card | undefined)[];
  dealerValueLine: string | null;
  sideBetRows: PlaySideBetRow[];
  insuranceResult: { win: boolean; label: string } | null;
  results: PlayResultRow[];

  // Insurance
  insuranceOffered: boolean;
  insuranceAmount: number;

  // Hold'em
  myHoldemTurn: boolean;
  holdemActions: PlayHoldemActionsVM | null;
  holeCards: Card[];
  holeLabel: string | null;
  holdemPlaceholder: string;
  boardCards: (Card | undefined)[];
  potLine: string | null;
  holdemWaitingForOther: boolean;
  holdemPayoutLine: PlayPayoutLine | null;

  // Baccarat
  isBaccarat: boolean;
  baccaratBet: { rules: BaccaratRules } | null;
  baccaratView: {
    playerCards: (Card | undefined)[];
    bankerCards: (Card | undefined)[];
    playerTotal: number | null;
    bankerTotal: number | null;
    outcomeLabel: string | null;
    backedLabel: string | null;
    result: { win: boolean; loss: boolean; label: string } | null;
    sideNotices: { key: string; tone: string; text: string }[];
    placeholder: string;
    hasCards: boolean;
  } | null;

  // Roulette
  isRoulette: boolean;
  /**
   * The layout panel. It shows for any seated player, not only while the
   * window is open: stacks stay on the felt through the spin and the settle
   * beat, so the phone has to keep painting them.
   */
  rouletteBet: {
    rules: RouletteRules;
    /** Every stack on the layout, this seat's marked as its own. */
    stacks: RouletteStackMap;
    /** The landed pocket, for the dolly — only once the spin has settled. */
    pocket: RoulettePocket | null;
    /** Spots the spin paid, for the winner's flash. */
    marks: Partial<Record<string, RouletteSpotMark>>;
    /** False once the window closes: the felt shows, but takes no new chips. */
    canDrop: boolean;
  } | null;
  rouletteView: {
    /** The pocket the ball landed on, once settle has published it. */
    pocket: string | null;
    pocketColor: 'red' | 'black' | 'green' | null;
    /** This seat's stacks, newest last, with labels the phone can print. */
    myBets: {
      id: RouletteBetId;
      num?: RouletteBetNum;
      key: string;
      label: string;
      amount: number;
      payout: number | null;
      win: boolean | null;
    }[];
    myStaked: number;
    done: boolean;
    doneLabel: string | null;
    /** The previous spin's spread, for the rebet button. Empty before one. */
    lastBets: RouletteBetSpec[];
    result: { win: boolean; loss: boolean; label: string } | null;
    placeholder: string;
    /** The last pockets, oldest first, for the ribbon. */
    history: { pocket: string; color: 'red' | 'black' | 'green' }[];
    /** The shared roller: same pocket and deadline the TV feeds its wheel. */
    wheel: {
      wheelOrder: readonly RoulettePocket[];
      landedIndex: number | null;
      pocket: string | null;
      pocketColor: 'red' | 'black' | 'green' | null;
    };
  } | null;

  // Keno
  isKeno: boolean;
  /**
   * The ticket panel. It shows for any seated player, not only while the
   * window is open: the board keeps the last draw and this seat's catches
   * through the settle beat and the payout.
   */
  kenoBet: { rules: KenoRules; canDrop: boolean } | null;
  kenoView: {
    /**
     * This seat's tickets, oldest first, phone-ready labels attached.
     * `colorIndex` is the ticket's table colour — the one the TV paints it.
     */
    myTickets: (PublicKenoTicket & {
      key: string;
      label: string;
      resultLabel: string | null;
      colorIndex: number;
    })[];
    /** Lounge-card hues when the host asked for them; otherwise seat colours. */
    classicColors: boolean;
    myStaked: number;
    done: boolean;
    doneLabel: string | null;
    /** The previous draw's slate, for the rebet button. Empty before one. */
    lastTickets: { spots: number[]; amount: number }[];
    result: { win: boolean; loss: boolean; label: string } | null;
    placeholder: string;
    /** The draw the board shows, once the numbers are out. */
    boardDraw: number[] | null;
    /** True once the draw has paid; before that the board reveals on the deadline. */
    settled: boolean;
  } | null;

  // Craps
  isCraps: boolean;
  /**
   * The layout panel. It shows for any seat in the game, not only while the
   * window is open: chips ride between hands, and their owner may pull them
   * whenever, so the felt has to stay on screen to be pulled from.
   */
  crapsBet: {
    rules: CrapsRules;
    point: number | null;
    /** False between hands: the felt shows, but takes no new chips. */
    canDrop: boolean;
    /** True only in the come-out window, where Ready means something. */
    showReady: boolean;
    /** True once the dice are live, whoever is holding them. */
    rolling: boolean;
    /** True when the ROLL button is this seat's to press. */
    canRoll: boolean;
    /** Who has the dice, for the button that waits on them. */
    shooterName: string | null;
    /** Every stake on the layout, this seat's marked as its own. */
    stacks: CrapsStackMap;
    /** What the last roll did, spot by spot. */
    marks: Partial<Record<CrapsBetId, CrapsSpotMark>>;
    myBets: PublicCrapsBet[];
    /**
     * Place and hardway bets of this seat's that the last roll just paid, for
     * as long as the answer is still live — the beat between the payout and
     * the next roll, where a real box asks whether to press it.
     */
    hits: {
      betId: CrapsBetId;
      label: string;
      /** The stake the roll paid on. */
      stake: number;
      /** Chips it returned, that stake included. */
      payout: number;
      /** What is on the number right now off the back of that hit. */
      rides: number;
    }[];
  } | null;
  crapsView: {
    point: number | null;
    /** "Come out — puck OFF" or "Point 6". */
    puckLabel: string;
    lastRoll: CrapsRoll | null;
    /** The published roll counter; the dome's throw trigger. */
    rollNumber: number;
    /** What that roll did, in words. */
    rollLine: string | null;
    shooterName: string | null;
    amShooter: boolean;
    /** True when the ROLL button is live: this seat holds the dice. */
    canRoll: boolean;
    myBets: {
      id: CrapsBetId;
      key: string;
      label: string;
      amount: number;
      working: boolean;
      payout: number | null;
      win: boolean | null;
    }[];
    myStaked: number;
    ready: boolean;
    readyLabel: string | null;
    /**
     * The single control under the chip rail — Ready, Roll, or a waiting
     * label. Same vocabulary as web's CrapsBet primary slot.
     */
    primaryCta: { label: string; armed: boolean; action: 'roll' | 'ready' | null };
    /** What the last roll did to this seat's bets. */
    notices: { key: string; tone: 'win' | 'loss' | 'push'; text: string }[];
    result: { win: boolean; loss: boolean; label: string } | null;
    placeholder: string;
    /** The last rolls, oldest first, for the ribbon. */
    history: { key: string; dice: CrapsRoll; total: number; hard: boolean }[];
  } | null;

  tableView: PlayTableViewVM | null;

  // Three-card poker
  isThreecard: boolean;
  threecardBet: { rules: ThreecardRules } | null;
  threecardView: {
    /** My three cards, as the private payload carries them. */
    myCards: Card[];
    myHandLabel: string | null;
    /** True while this seat holds its play-or-fold decision. */
    canDecide: boolean;
    /** True when the stack still covers the matching play bet. */
    canPlay: boolean;
    decisionHint: string | null;
    folded: boolean;
    ante: number;
    play: number;
    pairPlusStake: number;
    pairPlusPayout: number | null;
    dealerCards: (Card | undefined)[];
    dealerLabel: string | null;
    dealerQualifies: boolean | null;
    result: { win: boolean; loss: boolean; label: string } | null;
    notices: { key: string; tone: 'win' | 'loss' | 'push'; text: string }[];
    placeholder: string;
    hasCards: boolean;
    history: { key: string; outcome: ThreecardOutcome; label: string }[];
  } | null;
}

export interface PlayModelOptions {
  /** Seats this phone already held per the join ack, before the first broadcast. */
  joinSeats?: readonly number[];
  /** The seat the controls point at when nothing else claims them. */
  focusSeat?: number | null;
  /** Seats that already answered the insurance offer this window. */
  answeredInsurance?: readonly number[];
  /** The room_closed reason, while the join form is showing. */
  notice?: string | null;
  /** Whether the phone's socket is currently connected. */
  online?: boolean;
  /**
   * Signed-in casino owner. They run the table even when `hostPlayerId` is
   * null (the casino rule), so Deal and the host panel show in-game.
   */
  isOwner?: boolean;
  /** Which of the phone's three screens is showing, for the nav strip. */
  view?: PlayView;
  /** True on a casino table: the phone gets a third screen, the lobby. */
  hasCasino?: boolean;
  /**
   * Local join identity while `room_state` has not arrived (or to fill gaps
   * the public seat has not named yet). Web and native both keep a stored
   * seat with these fields.
   */
  joinName?: string | null;
  joinCode?: string | null;
}

/** Street names for the table view. */
const STREET_LABELS: Record<string, string> = {
  preflop: 'Hole cards',
  flop: 'Flop',
  turn: 'Turn',
  river: 'River',
};

const SPOT_LABELS: Record<BaccaratSpot, string> = {
  player: 'Player',
  banker: 'Banker',
  tie: 'Tie',
};

export const handValueLabel = (h: PrivateHandView): string =>
  `${h.value}${h.soft && h.value !== 21 ? ' soft' : ''}${h.value > 21 ? ' — bust' : ''}`;

/** Table view speaks only in public terms: what the face-up cards add to. */
export const publicHandValueLabel = (h: PublicHand): string => {
  if (h.visibleValue == null) return '…';
  const bust = h.status === 'busted' ? ' — bust' : '';
  const soft = h.visibleSoft && !bust ? ' soft' : '';
  return `${h.visibleValue}${soft}${bust}`;
};

export const seatStatusLabel = (status: PublicSeat['status']): string => {
  if (status === 'waiting') return 'Waiting';
  if (status === 'sitting_out') return 'Sitting out';
  if (status === 'betting') return 'Betting…';
  if (status === 'ready') return 'Bet in';
  if (status === 'acting') return 'Their turn';
  if (status === 'done') return 'Done';
  return '';
};

export const handResultLabel = (h: PublicHand): string | null => {
  if (!h.result) return null;
  const net = h.payout != null ? h.payout - h.bet : null;
  if (h.result === 'blackjack') return `Blackjack! ${formatSignedChips(net ?? 0)}`;
  if (h.result === 'win') return `Win ${formatSignedChips(net ?? 0)}`;
  if (h.result === 'push') return 'Push — bet returned';
  if (h.result === 'surrender') return `Surrendered ${formatSignedChips(net ?? -h.bet)}`;
  return `Dealer takes ${formatSignedChips(net ?? -h.bet)}`;
};

export const deadlineLabel = (
  kind: PublicDeadline['kind'] | null,
  phase: Phase,
  gameType: GameType,
): string => {
  if (kind === 'break' && phase === 'dealer' && gameType === 'keno') return 'Draw lands';
  if (kind === 'break' && phase === 'rolling' && gameType === 'craps') return 'Dice back';
  if (kind === 'bet') return 'Betting closes';
  if (kind === 'action') return 'Your turn ends';
  if (kind === 'insurance') return 'Insurance closes';
  return 'Next round';
};

const blackjackPlaceholder = (phase: Phase, canDeal: boolean, hostName: string | null): string =>
  phase === 'betting'
    ? 'Waiting for the table to finish betting…'
    : phase === 'insurance'
      ? 'Insurance decision at the table…'
      : phase === 'playing' || phase === 'dealer'
        ? 'Cards are on the table…'
        : phase === 'payout'
          ? 'Settling the round…'
          : canDeal
            ? 'You run the table — deal when ready.'
            : `Waiting for ${hostName ?? 'the table'} to deal.`;

const holdemPlaceholder = (phase: Phase, canDeal: boolean, hostName: string | null): string =>
  phase === 'payout'
    ? 'Settling the hand…'
    : canDeal
      ? 'You run the table — deal when ready.'
      : `Waiting for ${hostName ?? 'the table'} to deal.`;

const baccaratPlaceholder = (
  phase: Phase,
  canDeal: boolean,
  hostName: string | null,
  canBet: boolean,
): string =>
  canBet ? 'Back a spot and stake your chips.' : blackjackPlaceholder(phase, canDeal, hostName);

const windowGameIdleCopy = (phase: Phase, canDeal: boolean): string | null => {
  if (phase === 'payout') return 'Settling the round…';
  // When Deal is armed the host has to open it — do not promise auto-open.
  if (phase === 'lobby') return canDeal ? null : 'The betting window opens automatically.';
  return null;
};

const roulettePlaceholder = (
  phase: Phase,
  canDeal: boolean,
  hostName: string | null,
  canBet: boolean,
): string => {
  if (canBet) return 'Spread chips across the layout, then tap Done.';
  if (phase === 'dealer') return 'No more bets — the ball is rolling.';
  const idle = windowGameIdleCopy(phase, canDeal);
  if (idle) return idle;
  return blackjackPlaceholder(phase, canDeal, hostName);
};

const kenoPlaceholder = (
  phase: Phase,
  canDeal: boolean,
  hostName: string | null,
  canBet: boolean,
): string => {
  if (canBet) return 'Fill a ticket and lock in — the house draws twenty.';
  if (phase === 'dealer') return 'No more bets — the balls are falling.';
  const idle = windowGameIdleCopy(phase, canDeal);
  if (idle) return idle;
  return blackjackPlaceholder(phase, canDeal, hostName);
};

/**
 * Nobody deals a craps table — the come-out window opens on its own once the
 * felt is idle and someone can bet. Deal stays as the escape hatch when every
 * solvent seat is sitting out (intentional sit-out / rules change).
 */
const crapsDealLabel = (phase: Phase): string =>
  phase === 'payout' ? 'Next come-out' : 'Open the come-out';

/**
 * Mirror of the server's `windowGameCanOpen`: true when the lobby would open
 * a roulette / keno / craps window without the host tapping Deal.
 */
const windowGameWouldAutoOpen = (state: RoomState): boolean => {
  if (state.phase !== 'lobby') return false;
  const minBet =
    state.gameType === 'craps'
      ? state.crapsRules.minBet
      : state.gameType === 'keno'
        ? state.kenoRules.minBet
        : state.gameType === 'roulette'
          ? state.rouletteRules.minBet
          : null;
  if (minBet == null) return false;
  const occupied = state.seats.filter((s) => s.playerId != null);
  if (occupied.length === 0) return false;
  for (const seat of occupied) {
    if (!seat.connected || seat.status === 'sitting_out') continue;
    // Craps riding chips show on seat.bet between hands.
    if (seat.bet > 0 || seat.chips >= minBet) return true;
  }
  return false;
};

/**
 * One button under the felt for the whole hand — same strings web's CrapsBet
 * primary slot prints. Ready opens the dice; the shooter rolls; every other
 * beat names who the table is waiting on.
 */
export const crapsPrimaryCta = (input: {
  canRoll: boolean;
  rolling: boolean;
  showReady: boolean;
  ready: boolean;
  staked: number;
  shooterName: string | null;
  /** False while nobody at the table has Pass or Don't Pass up to shoot on. */
  lineUp: boolean;
}): { label: string; armed: boolean; action: 'roll' | 'ready' | null } => {
  if (input.canRoll) return { label: 'Roll the dice', armed: true, action: 'roll' };
  if (input.rolling) {
    return {
      label: input.shooterName ? `${input.shooterName} is shooting` : 'The dice are out',
      armed: false,
      action: null,
    };
  }
  if (!input.showReady) return { label: 'Between hands', armed: false, action: null };
  if (input.ready) {
    return {
      label: input.lineUp ? 'Locked in' : 'Locked in — the dice need a line bet',
      armed: true,
      action: null,
    };
  }
  if (!input.lineUp) {
    return {
      label: input.staked === 0 ? 'Nothing down — the dice need a line bet' : 'Ready — the dice need a line bet',
      armed: false,
      action: 'ready',
    };
  }
  return {
    label: input.staked === 0 ? 'Nothing down — send them out' : 'Ready — send them out',
    armed: false,
    action: 'ready',
  };
};

const crapsPlaceholder = (
  phase: Phase,
  canDeal: boolean,
  hostName: string | null,
  point: number | null,
  amShooter: boolean,
): string => {
  if (phase === 'betting') return 'Back the line, then anything else. Ready sends the dice out.';
  if (phase === 'rolling') {
    if (amShooter) return point === null ? 'Your come-out — roll them.' : `Point ${point} — roll.`;
    return point === null ? 'Come-out roll on its way.' : `The shooter is going for the ${point}.`;
  }
  const idle = windowGameIdleCopy(phase, canDeal);
  if (idle) return idle;
  return blackjackPlaceholder(phase, canDeal, hostName);
};

const threecardPlaceholder = (
  phase: Phase,
  canDeal: boolean,
  hostName: string | null,
  canBet: boolean,
): string => {
  if (canBet) return 'Post your ante — Pair Plus rides beside it if you want it.';
  if (phase === 'playing') return 'Deciding at the table…';
  return blackjackPlaceholder(phase, canDeal, hostName);
};

/** The history strip reads the five hand outcomes back in words. */
export const THREECARD_OUTCOME_LABELS: Record<ThreecardOutcome, string> = {
  ante: 'Ante',
  play: 'Play',
  dealer: 'Dealer',
  push: 'Push',
  fold: 'Fold',
};

/** One line of commentary on what a roll did to one of this seat's bets. */
const crapsNotice = (
  s: PublicCraps['lastSettlements'][number],
): { tone: 'win' | 'loss' | 'push'; text: string } => {
  const label = CRAPS_BETS[s.betId].label;
  if (s.resolution === 'win') {
    // A place or hardway bet that stayed up says so: the win is the profit,
    // and whatever is still on the number is what the next roll plays for.
    const rides = s.rides > 0 ? ` · ${s.rides} rides` : '';
    return {
      tone: 'win',
      text: `${label} ${s.amount} → ${formatSignedChips(s.payout - s.amount)}${rides}`,
    };
  }
  if (s.resolution === 'push') return { tone: 'push', text: `${label} ${s.amount} — stake back` };
  return { tone: 'loss', text: `${label} — ${formatSignedChips(-s.amount)}` };
};

const crapsRowResult = (
  result: { stake: number; payout: number },
): { win: boolean; loss: boolean; label: string } => {
  const net = result.payout - result.stake;
  if (net === 0) return { win: false, loss: false, label: 'Even — stakes came back' };
  return { win: net > 0, loss: net < 0, label: formatSignedChips(net) };
};

const baccaratOutcomeLabel = (bac: PublicBaccarat): string | null => {
  if (!bac.outcome) return null;
  const base = bac.outcome === 'tie' ? 'Tie' : `${SPOT_LABELS[bac.outcome]} wins`;
  return bac.natural ? `${base} · natural` : base;
};

const baccaratMainResult = (
  result: PublicBaccaratResult,
  rules: BaccaratRules | null,
): { win: boolean; loss: boolean; label: string } => {
  const net = result.payout - result.stake;
  const win = result.payout > result.stake;
  const loss = result.payout < result.stake;
  if (result.payout === result.stake) {
    return { win: false, loss: false, label: 'Push — bet returned' };
  }
  if (win && result.spot === 'tie' && rules) {
    return { win, loss, label: `Tie pays ${rules.tiePayout}:1 · ${formatSignedChips(net)}` };
  }
  return { win, loss, label: formatSignedChips(net) };
};

const baccaratRowResult = (result: PublicBaccaratResult): string =>
  result.payout === result.stake
    ? 'Push — bet returned'
    : formatSignedChips(result.payout - result.stake);

export const pocketLabel = (pocket: number | '00'): string => String(pocket);

const rouletteBetLabel = (bet: PublicRouletteBet): string => {
  const def = ROULETTE_BETS[bet.id];
  return bet.num !== undefined ? `${def.label} ${bet.num}` : def.label;
};

const rouletteRowResult = (
  result: { payout: number },
  stake: number,
): { win: boolean; loss: boolean; label: string } => {
  const net = result.payout - stake;
  if (result.payout === 0) return { win: false, loss: true, label: `Lost ${stake.toLocaleString()}` };
  if (net === 0) return { win: false, loss: false, label: 'Push — stake came back' };
  return { win: net > 0, loss: net < 0, label: formatSignedChips(net) };
};

const faceUpOnly = (cards: readonly PublicCard[]): (Card | undefined)[] =>
  cards.map((c) => (c.faceUp ? c.card : undefined));

const playerFor = (state: RoomState | null, playerId: string | null): PublicPlayer | null =>
  (state && playerId ? state.players.find((p) => p.playerId === playerId) ?? null : null);

/**
 * Which seat the controls drive. Whatever the table is waiting on wins, so a
 * player holding several seats is never left tapping the wrong one.
 */
/**
 * A key that changes each time the phone should pull the roller into view:
 * once when the ball is released, once when the dice go out, and again on
 * every throw. Stable across identical room_state echoes so a rebroadcast
 * does not yank the scroll mid-animation.
 */
export function playAnimFocusKey(state: RoomState | null): string | null {
  if (!state) return null;
  if (state.gameType === 'roulette' && state.phase === 'dealer') {
    return `roulette-spin-${state.roulette?.spinNumber ?? state.round}`;
  }
  if (state.gameType === 'keno' && state.phase === 'dealer') {
    return `keno-draw-${state.keno?.drawNumber ?? state.round}`;
  }
  // A roll that ends the hand lands in payout. Under reduced motion nothing
  // holds the reading back, so payout keys the same roll the throw would have.
  if (state.gameType === 'craps' && (state.phase === 'rolling' || state.phase === 'payout')) {
    return `craps-roll-${state.round}-${state.craps?.rollNumber ?? 0}`;
  }
  return null;
}

export const resolveSeatIndex = (
  state: RoomState | null,
  mySeats: number[],
  fallback: number,
  phase: Phase,
  focusSeat: number | null,
  answeredInsurance: readonly number[],
): number => {
  if (mySeats.length === 0) return fallback;
  if (state?.activeSeatIndex != null && mySeats.includes(state.activeSeatIndex)) {
    return state.activeSeatIndex;
  }
  if (phase === 'betting') {
    const owing = mySeats.find((i) => state?.seats[i]?.status === 'betting');
    if (owing !== undefined) return owing;
  }
  if (phase === 'insurance') {
    const pending = mySeats.find(
      (i) => (state?.seats[i]?.bet ?? 0) > 0 && !answeredInsurance.includes(i),
    );
    if (pending !== undefined) return pending;
  }
  if (focusSeat !== null && mySeats.includes(focusSeat)) return focusSeat;
  return mySeats[0]!;
};

export function playModel(
  state: RoomState | null,
  hand: PrivateHand | null,
  playerId: string | null,
  opts: PlayModelOptions = {},
): PlayVM {
  const {
    joinSeats,
    focusSeat = null,
    answeredInsurance = [],
    notice = null,
    online = true,
    isOwner = false,
    view = 'controls',
    hasCasino = false,
    joinName = null,
    joinCode = null,
  } = opts;

  const phase: Phase = state?.phase ?? 'lobby';
  const mySeats: number[] =
    playerId
      ? playerFor(state, playerId)?.seats ?? [...(joinSeats ?? [])]
      : [...(joinSeats ?? [])];
  const mySeatIndex = resolveSeatIndex(
    state,
    mySeats,
    joinSeats?.[0] ?? 0,
    phase,
    focusSeat,
    answeredInsurance,
  );
  const mySeat: PublicSeat | null = state ? state.seats[mySeatIndex] ?? null : null;
  const myPlayer = playerFor(state, playerId);
  // The standing reservation, not the per-phase status: it survives rounds.
  const ready = mySeat?.lobbyReady ?? false;
  const betting = phase === 'betting' && mySeat?.status === 'betting';
  const myTurn = phase === 'playing' && mySeat?.status === 'acting';
  const options = myTurn ? mySeat?.options ?? null : null;
  const activeHandIndex = mySeat?.activeHandIndex ?? null;
  const myHands = hand?.seats.find((s) => s.seatIndex === mySeatIndex)?.hands ?? [];
  const hostName = state?.players.find((p) => p.isHost)?.name ?? null;
  // Whoever took the first seat — quick table or casino membership — deals
  // from this phone; the signed-in casino owner outranks them either way.
  const amHost =
    (state != null && state.hostPlayerId != null && state.hostPlayerId === playerId) || isOwner;
  const roomCode = state?.code ?? joinCode ?? null;
  const playerName = myPlayer?.name ?? mySeat?.name ?? joinName ?? null;
  const roleLine = amHost ? 'You run the table' : null;
  const seatInitials = profileInitials(playerName ?? '');
  const chips = myPlayer?.chips ?? mySeat?.chips ?? 0;
  const rules = state?.rules ?? null;
  const baccaratRules = state?.baccaratRules ?? null;
  const rouletteRules = state?.rouletteRules ?? null;
  const threecardRules = state?.threecardRules ?? null;
  const isHoldem = state?.gameType === 'holdem';
  const isBaccarat = state?.gameType === 'baccarat';
  const isRoulette = state?.gameType === 'roulette';
  const isCraps = state?.gameType === 'craps';
  const isThreecard = state?.gameType === 'threecard';
  const isKeno = state?.gameType === 'keno';
  const canDeal =
    amHost &&
    (phase === 'lobby' || phase === 'payout') &&
    // Window games open on their own when someone can bet; Deal stays when
    // every chair is sitting out so the host is not trapped in the lobby.
    (!(isRoulette || isCraps || isKeno) || !state || !windowGameWouldAutoOpen(state));
  const crapsRules = state?.crapsRules ?? null;
  const kenoRules = state?.kenoRules ?? null;
  const craps = state?.craps ?? null;
  const holdem = state?.holdem ?? null;
  const baccarat = state?.baccarat ?? null;
  const roulette = state?.roulette ?? null;
  const threecard = state?.threecard ?? null;
  const keno = state?.keno ?? null;
  const myHoldem = holdem?.seats[mySeatIndex] ?? null;
  const myHoldemSeat = hand?.holdem?.find((s) => s.seatIndex === mySeatIndex) ?? null;
  const myHoldemTurn =
    isHoldem && holdem != null && state?.activeSeatIndex === mySeatIndex && myHoldem?.options != null;
  const holdemOptions = myHoldemTurn ? myHoldem!.options! : null;
  const myHoldemNet = myHoldem?.won != null ? myHoldem.won - myHoldem.totalCommitted : null;
  const openSeats = state ? state.seats.filter((s) => s.playerId === null).map((s) => s.index) : [];
  const seatCap = isBaccarat
    ? baccaratRules?.seatsPerPlayer
    : isRoulette
      ? rouletteRules?.seatsPerPlayer
      : isCraps
        ? crapsRules?.seatsPerPlayer
        : isThreecard
          ? threecardRules?.seatsPerPlayer
          : isKeno
            ? kenoRules?.seatsPerPlayer
            : rules?.seatsPerPlayer;
  const canTakeAnother =
    !isHoldem && seatCap != null && mySeats.length < seatCap && openSeats.length > 0;
  const minStake = isBaccarat
    ? baccaratRules?.minBet
    : isRoulette
      ? rouletteRules?.minBet
      : isCraps
        ? crapsRules?.minBet
        : isThreecard
          ? // Ante needs a matching play bet; broke means short of both.
            threecardRules != null
              ? threecardRules.minBet * 2
              : undefined
          : isKeno
            ? kenoRules?.minBet
            : isHoldem
              ? state!.holdemRules.bigBlind
              : rules?.minBet;
  const broke = minStake != null && chips < minStake;
  const canRebuy = isBaccarat
    ? baccaratRules?.rebuyAllowed ?? false
    : isRoulette
      ? rouletteRules?.rebuyAllowed ?? false
      : isCraps
        ? crapsRules?.rebuyAllowed ?? false
        : isThreecard
          ? threecardRules?.rebuyAllowed ?? false
          : isKeno
            ? kenoRules?.rebuyAllowed ?? false
            : isHoldem
              ? state?.holdemRules.rebuyAllowed ?? false
              : rules?.rebuyAllowed ?? false;
  const buyIn = rules?.startingChips ?? 0;
  const insuranceOffered =
    phase === 'insurance' &&
    !answeredInsurance.includes(mySeatIndex) &&
    (mySeat?.bet ?? 0) > 0 &&
    (mySeat?.insuranceBet ?? 0) === 0;

  // Only a seated phone that is not already running the table can take it.
  const canClaimHost =
    (state?.hostClaimable ?? false) && !amHost && mySeats.length > 0;

  // Banners: own seat dropped server-side, or the host vanished mid-grace.
  let banner: string | null = null;
  if (mySeat != null && !mySeat.connected) {
    banner = 'Reconnecting…';
  } else if (canClaimHost) {
    banner = 'The host is gone — take over the table to keep playing.';
  } else if (
    state != null &&
    state.hostPlayerId != null &&
    !amHost &&
    !(state.players.find((p) => p.playerId === state.hostPlayerId)?.connected ?? false)
  ) {
    banner = 'The host lost connection — waiting for them to come back…';
  }

  const myHandsRows: PlayHandRow[] = myHands.map((h, i) => ({
    cards: h.cards,
    label: `${handValueLabel(h)}${h.bet > 0 ? ` · ${h.bet}` : ''}`,
    active: myHands.length > 1 && i === activeHandIndex,
    bet: h.bet,
  }));

  const tableView = state
    ? buildTableView(state, hand, isHoldem, isBaccarat, mySeats, phase)
    : null;

  const myBaccarat = baccarat?.seats[mySeatIndex] ?? null;
  const myBaccaratResult = baccarat?.results?.find((r) => r.seatIndex === mySeatIndex) ?? null;

  const myRoulette = roulette?.seats[mySeatIndex] ?? null;
  const myRouletteStaked = (myRoulette?.bets ?? []).reduce((sum, b) => sum + b.amount, 0);
  const myRouletteResult = roulette?.results?.find((r) => r.seatIndex === mySeatIndex) ?? null;
  const rouletteLocked = phase === 'betting' && mySeat?.status === 'ready';
  const rouletteSettled = roulette?.results != null;
  const rouletteOrder = rouletteRules ? wheelOrder(rouletteRules.wheel) : [];
  const rouletteView: PlayVM['rouletteView'] = isRoulette
    ? {
        pocket:
          rouletteSettled && roulette?.pocket != null ? pocketLabel(roulette.pocket) : null,
        pocketColor:
          rouletteSettled && roulette?.pocket != null ? pocketColor(roulette.pocket) : null,
        myBets: (myRoulette?.bets ?? []).map((b, i) => ({
          id: b.id,
          ...(b.num !== undefined ? { num: b.num } : {}),
          key: `${b.id}-${b.num ?? 'x'}-${i}`,
          label: rouletteBetLabel(b),
          amount: b.amount,
          payout: b.payout,
          win: b.payout == null ? null : b.payout > 0,
        })),
        myStaked: myRouletteStaked,
        done: myRoulette?.done ?? false,
        doneLabel: rouletteLocked
          ? `Locked in for ${myRouletteStaked.toLocaleString()} — take back to change`
          : null,
        lastBets: (myRoulette?.lastBets ?? []).map((b) => ({ ...b })),
        result:
          myRouletteResult && mySeat
            ? rouletteRowResult(myRouletteResult, mySeat.bet)
            : null,
        placeholder: roulettePlaceholder(phase, canDeal, hostName, betting),
        history: (roulette?.history ?? []).slice(-18).map((p) => ({
          pocket: pocketLabel(p),
          color: pocketColor(p),
        })),
        wheel: {
          wheelOrder: rouletteOrder,
          landedIndex:
            roulette?.pocket != null ? rouletteOrder.indexOf(roulette.pocket) : null,
          // The plate waits for the ball; calling the number in text waits for settle.
          pocket: roulette?.pocket != null ? pocketLabel(roulette.pocket) : null,
          pocketColor: roulette?.pocket != null ? pocketColor(roulette.pocket) : null,
        },
      }
    : null;

  const myKeno = keno?.seats[mySeatIndex] ?? null;
  const myKenoStaked = (myKeno?.tickets ?? []).reduce((sum, t) => sum + t.amount, 0);
  const myKenoResult = keno?.results?.find((r) => r.seatIndex === mySeatIndex) ?? null;
  const kenoLocked = phase === 'betting' && mySeat?.status === 'ready';
  const kenoSettled = keno?.results != null;
  const kenoClassicColors = kenoRules?.classicColors ?? false;
  const kenoSeats = keno?.seats ?? [];
  const kenoView: PlayVM['kenoView'] = isKeno
    ? {
        myTickets: (myKeno?.tickets ?? []).map((t, i) => ({
          ...t,
          key: `${t.id}-${i}`,
          label: describeKenoTicket(t.spots),
          resultLabel: ticketResultLabel(t.amount, t.payout),
          colorIndex: kenoTicketColorIndex(kenoSeats, mySeatIndex, i),
        })),
        classicColors: kenoClassicColors,
        myStaked: myKenoStaked,
        done: myKeno?.done ?? false,
        doneLabel: kenoLocked
          ? `Locked in for ${myKenoStaked.toLocaleString()} — take back to change`
          : null,
        lastTickets: (myKeno?.lastTickets ?? []).map((t) => ({ spots: [...t.spots], amount: t.amount })),
        result:
          myKenoResult && mySeat
            ? {
                win: myKenoResult.payout > myKenoResult.stake,
                loss: myKenoResult.payout < myKenoResult.stake,
                label:
                  myKenoResult.payout === myKenoResult.stake
                    ? 'Push — stakes came back'
                    : formatSignedChips(myKenoResult.payout - myKenoResult.stake),
              }
            : null,
        placeholder: kenoPlaceholder(phase, canDeal, hostName, betting),
        // The settled board keeps the numbers up; mid-window there is nothing
        // to show and the phone prints its picker over an empty board.
        boardDraw: kenoSettled || phase === 'dealer' ? keno?.draw ?? null : null,
        // Until settle the phone lights the draw on the TV's clock, not at once.
        settled: kenoSettled,
      }
    : null;

  const myCraps = craps?.seats[mySeatIndex] ?? null;
  const myCrapsStaked = crapsSeatStaked(craps ?? null, mySeatIndex);
  const myCrapsResult = craps?.results?.find((r) => r.seatIndex === mySeatIndex) ?? null;
  const amShooter = isCraps && craps?.shooterSeatIndex === mySeatIndex;
  // The last roll's settle beat runs as a `break`: the dice are not back yet.
  const canRoll = isCraps && phase === 'rolling' && amShooter && state?.deadline?.kind !== 'break';
  const canDecide =
    isThreecard &&
    phase === 'playing' &&
    mySeat?.status === 'acting' &&
    state?.activeSeatIndex === mySeatIndex;

  const myThreecard = threecard?.seats[mySeatIndex] ?? null;
  const myThreecardHand = hand?.threecard?.find((s) => s.seatIndex === mySeatIndex) ?? null;
  const myThreecardResult = threecard?.results?.find((r) => r.seatIndex === mySeatIndex) ?? null;
  const threecardAnte = mySeat?.bet ?? 0;
  const canPlayThreecard = canDecide && chips >= threecardAnte;
  const threecardView = isThreecard
    ? {
        myCards: myThreecardHand?.cards ?? [],
        myHandLabel: myThreecardHand?.rank?.label ?? null,
        canDecide,
        canPlay: canPlayThreecard,
        decisionHint: canDecide
          ? canPlayThreecard
            ? `Play for ${threecardAnte.toLocaleString()}, or muck it.`
            : 'Not enough chips to play — fold, or the clock will.'
          : myThreecardHand && myThreecardHand.cards.length > 0 && !myThreecard?.folded
            ? 'Waiting on the other antes…'
            : null,
        folded: myThreecard?.folded ?? false,
        ante: threecardAnte,
        play: myThreecard?.play ?? 0,
        pairPlusStake: myThreecard?.pairPlus?.stake ?? 0,
        pairPlusPayout: myThreecard?.pairPlus?.payout ?? null,
        dealerCards: threecard ? faceUpOnly(threecard.dealerCards) : [],
        dealerLabel: threecard?.dealerLabel ?? null,
        dealerQualifies: threecard?.dealerQualifies ?? null,
        result: (() => {
          if (!myThreecardResult) return null;
          // Pair Plus has its own notice. The result line is ante + play only,
          // or a winning side bet would print twice.
          const pairPlusReturned = myThreecard?.pairPlus?.payout ?? 0;
          const staked = myThreecardResult.ante + myThreecardResult.play;
          const net = myThreecardResult.payout - pairPlusReturned - staked;
          if (myThreecardResult.outcome === 'fold') {
            return net < 0
              ? { win: false, loss: true, label: `Folded ${formatSignedChips(net)}` }
              : { win: false, loss: false, label: 'Folded — Pair Plus paid it back' };
          }
          if (net === 0) return { win: false, loss: false, label: 'Push — stakes came back' };
          return {
            win: net > 0,
            loss: net < 0,
            label: formatSignedChips(net),
          };
        })(),
        notices: [
          ...(myThreecard?.pairPlus && myThreecard.pairPlus.payout != null
            ? [
                {
                  key: 'pair_plus',
                  tone: (myThreecard.pairPlus.payout > 0
                    ? 'win'
                    : 'loss') as 'win' | 'loss' | 'push',
                  text: `Pair Plus ${formatSignedChips(
                    myThreecard.pairPlus.payout - myThreecard.pairPlus.stake,
                  )}`,
                },
              ]
            : []),
          ...(threecard?.dealerQualifies === false
            ? [
                {
                  key: 'no_qualify',
                  tone: 'win' as const,
                  text: 'Dealer doesn’t qualify — antes pay, play bets push',
                },
              ]
            : []),
        ],
        placeholder: threecardPlaceholder(phase, canDeal, hostName, betting),
        hasCards: (myThreecardHand?.cards.length ?? 0) > 0,
        history: (threecard?.history ?? []).slice(-18).map((outcome, i) => ({
          key: `${i}-${outcome}`,
          outcome,
          label: THREECARD_OUTCOME_LABELS[outcome],
        })),
      }
    : null;
  // The felt is live in the come-out window and through every roll. A seat that
  // sat the come-out out is shut only for the window; once the dice are
  // rolling its first chip buys it into the hand.
  const crapsSeatLive =
    isCraps &&
    mySeat != null &&
    mySeat.playerId !== null &&
    (mySeat.status !== 'sitting_out' || phase === 'rolling');
  const crapsCanBet = crapsSeatLive && (phase === 'betting' || phase === 'rolling');
  // Roulette stacks ride the spin and the settle beat the same way; the window
  // is the only time new chips can land.
  const rouletteSeatLive =
    isRoulette && mySeat != null && mySeat.status !== 'sitting_out' && mySeat.playerId !== null;
  const rouletteCanDrop = rouletteSeatLive && phase === 'betting';
  // Keno tickets ride the draw and the settle beat the same way.
  const kenoSeatLive =
    isKeno && mySeat != null && mySeat.status !== 'sitting_out' && mySeat.playerId !== null;
  const kenoCanDrop = kenoSeatLive && phase === 'betting';
  const crapsShooterName =
    craps?.shooterSeatIndex != null
      ? state?.seats[craps.shooterSeatIndex]?.name ?? null
      : null;
  const crapsView = isCraps
    ? {
        point: craps?.point ?? null,
        puckLabel: craps?.point == null ? 'Come out — puck OFF' : `Point ${craps.point}`,
        lastRoll: craps?.lastRoll ?? null,
        rollNumber: craps?.rollNumber ?? 0,
        rollLine: crapsRollCall(craps ?? null),
        shooterName: crapsShooterName,
        amShooter,
        canRoll,
        myBets: (myCraps?.bets ?? []).map((b: PublicCrapsBet) => ({
          id: b.id,
          key: b.id,
          label: CRAPS_BETS[b.id].label,
          amount: b.amount,
          working: b.working,
          press: b.press,
          payout: b.payout,
          win: b.payout == null ? null : b.payout > b.amount,
        })),
        myStaked: myCrapsStaked,
        ready: myCraps?.ready ?? false,
        readyLabel:
          phase === 'betting' && (myCraps?.ready ?? false)
            ? `Locked in for ${myCrapsStaked.toLocaleString()} — take back to change`
            : null,
        primaryCta: crapsPrimaryCta({
          canRoll,
          rolling: phase === 'rolling',
          showReady: phase === 'betting',
          ready: myCraps?.ready ?? false,
          staked: myCrapsStaked,
          shooterName: crapsShooterName,
          lineUp: (craps?.seats ?? []).some((cs) =>
            cs.bets.some((b) => b.id === 'pass' || b.id === 'dontPass'),
          ),
        }),
        notices: (craps?.lastSettlements ?? [])
          .filter((n) => n.seatIndex === mySeatIndex)
          .map((n, i) => ({ key: `${n.betId}-${i}`, ...crapsNotice(n) })),
        result: myCrapsResult ? crapsRowResult(myCrapsResult) : null,
        placeholder: crapsPlaceholder(phase, canDeal, hostName, craps?.point ?? null, amShooter),
        history: (craps?.rollHistory ?? []).slice(-12).map((dice, i) => ({
          key: `${i}-${dice[0]}${dice[1]}`,
          dice,
          total: rollTotal(dice),
          hard: isHardRoll(dice),
        })),
      }
    : null;

  return {
    phase,
    gameBadge: state ? gameLabel(state.gameType) : null,
    amHost,
    online,
    roomLabel: roomCode ? `Room ${roomCode}` : null,
    playerName,
    roleLine,
    seatInitials,
    seatPill: online ? `Seat ${mySeatIndex + 1}` : 'Reconnecting…',

    mySeats,
    mySeatIndex,
    mySeat,
    myPlayer,
    seatTabs: mySeats.map((index) => {
      const s = state?.seats[index];
      return {
        index,
        label: `Seat ${index + 1}${s?.bet ? ` · ${s.bet}` : ''}`,
        active: index === mySeatIndex,
      };
    }),
    canReleaseSeat: mySeats.length > 1,
    navTabs: [
      { id: 'controls' as const, label: 'My controls', active: view === 'controls' },
      {
        id: 'table' as const,
        // One vocabulary across games — web always says "The table".
        label: 'The table',
        active: view === 'table',
      },
      ...(hasCasino
        ? [{ id: 'casino' as const, label: 'Casino', active: view === 'casino' }]
        : []),
    ],
    view,

    chipsAvailable: chips,
    hostName,
    deadline: state?.deadline
      ? { endsAt: state.deadline.endsAt, label: deadlineLabel(state.deadline.kind, phase, state.gameType) }
      : null,

    banner,
    canClaimHost,
    notice,

    // Only a seated phone has a ballot; a spectator watches the bars move.
    gamePoll: gamePollModel(state?.gamePoll, {
      playerId,
      canVote: mySeats.length > 0,
    }),

    ready,
    canDeal,
    dealLabel: canDeal ? (isCraps ? crapsDealLabel(phase) : phase === 'payout' ? 'Deal next round' : 'Deal') : null,
    canTakeAnother,
    openSeats,
    showHostPanel: amHost && state != null,
    // Craps has no lobby reservation to make: the come-out window is where a
    // seat declares itself, and chips ride across hands on their own. A second
    // Ready here would only compete with the one on the felt.
    showReadyToggle: (phase === 'lobby' || phase === 'payout') && !isCraps,
    readyLabel:
      (phase === 'lobby' || phase === 'payout') && !isCraps
        ? ready
          ? 'Step away'
          : "I'm ready"
        : null,

    isHoldem,
    canBet: betting,
    betControls:
      betting && !isBaccarat && !isRoulette && !isCraps && !isThreecard && !isKeno && rules
        ? { sideBets: rules.sideBets, sideBetMax: rules.sideBetMax, minBet: rules.minBet }
        : null,
    canClearBet:
      phase === 'betting' && mySeat?.status === 'ready' && !isRoulette && !isCraps && !isKeno,
    clearBetLabel:
      phase === 'betting' && mySeat?.status === 'ready' && !isRoulette && !isCraps && !isKeno
        ? `Take back my ${mySeat.bet} bet`
        : null,
    canSitOut: betting,
    canRebuy: broke && canRebuy,
    rebuyLabel: broke && canRebuy ? `Rebuy ${buyIn.toLocaleString()}` : null,
    isMyTurn: myTurn,
    canHit: myTurn && (options?.hit ?? false),
    canStand: myTurn && (options?.stand ?? false),
    canDouble: myTurn && (options?.double ?? false),
    canSplit: myTurn && (options?.split ?? false),
    canSurrender: myTurn && (options?.surrender ?? false),
    myHands: myHandsRows,
    handPlaceholder: blackjackPlaceholder(phase, canDeal, hostName),
    sittingOut: mySeat?.status === 'sitting_out' && phase !== 'lobby',
    waitingForOtherPlayer: phase === 'playing' && !myTurn && myHands.length > 0,
    dealerCards:
      state && !isHoldem && !isBaccarat && !isRoulette && !isCraps && !isThreecard && !isKeno
        ? faceUpOnly(state.dealer.cards)
        : [],
    dealerValueLine:
      state &&
      !isHoldem &&
      !isBaccarat &&
      !isRoulette &&
      !isCraps &&
      !isThreecard &&
      !isKeno &&
      state.dealer.cards.length > 0
        ? `Shows ${state.dealer.visibleValue ?? '…'}${
            state.dealer.blackjack ? ' — blackjack' : ''
          }`
        : null,
    sideBetRows: (mySeat?.sideBets ?? []).map((b) => ({
      key: b.id,
      win: Boolean(b.payout),
      label: b.payout
        ? `${b.label ?? `Side bet ${b.stake}`} — ${formatSignedChips(b.payout - b.stake)}`
        : `Side bet ${b.stake} — ${formatSignedChips(-b.stake)}`,
    })),
    insuranceResult:
      (mySeat?.insuranceBet ?? 0) > 0 && phase === 'payout'
        ? {
            win: Boolean(mySeat!.insurancePayout),
            label: `Insurance ${formatSignedChips(
              (mySeat!.insurancePayout ?? 0) - (mySeat!.insuranceBet ?? 0),
            )}`,
          }
        : null,
    results:
      phase === 'payout' && mySeat
        ? mySeat.hands
            .map((h, i) => {
              const label = handResultLabel(h);
              return label
                ? {
                    key: i,
                    className: h.result ? `result_${h.result}` : null,
                    label: `${mySeat.hands.length > 1 ? `Hand ${i + 1}: ` : ''}${label}`,
                  }
                : null;
            })
            .filter((r): r is PlayResultRow => r !== null)
        : [],

    insuranceOffered,
    insuranceAmount: Math.floor((mySeat?.bet ?? 0) / 2),

    myHoldemTurn,
    holdemActions:
      myHoldemTurn && holdemOptions && holdem
        ? {
            options: holdemOptions,
            chips,
            potTotal: holdem.potTotal,
            currentBet: holdem.currentBet,
            bigBlind: holdem.bigBlind,
          }
        : null,
    holeCards: myHoldemSeat?.cards ?? [],
    holeLabel:
      myHoldemSeat && myHoldemSeat.cards.length > 0
        ? `${myHoldemSeat.rank ? myHoldemSeat.rank.label : 'Hole cards'}${
            myHoldem && myHoldem.totalCommitted > 0
              ? ` · in ${myHoldem.totalCommitted.toLocaleString()}`
              : ''
          }`
        : null,
    holdemPlaceholder: holdemPlaceholder(phase, canDeal, hostName),
    boardCards: holdem ? faceUpOnly(holdem.board) : [],
    potLine:
      holdem && holdem.street !== null
        ? `Pot ${holdem.potTotal.toLocaleString()}${
            myHoldem && holdem.currentBet > myHoldem.committed
              ? ` · to call ${(holdem.currentBet - myHoldem.committed).toLocaleString()}`
              : ''
          }${myHoldem?.folded ? ' · you folded' : ''}${myHoldem?.allIn ? ' · all in' : ''}`
        : null,
    holdemWaitingForOther:
      holdem != null &&
      holdem.street !== null &&
      phase !== 'payout' &&
      !myHoldemTurn &&
      !myHoldem?.folded &&
      (myHoldemSeat?.cards.length ?? 0) > 0,
    holdemPayoutLine:
      phase === 'payout' && myHoldemNet !== null
        ? {
            win: myHoldemNet > 0,
            loss: myHoldemNet < 0,
            label: `${myHoldem?.handLabel ? `${myHoldem.handLabel} — ` : ''}${formatSignedChips(myHoldemNet)}`,
          }
        : null,

    isBaccarat,
    baccaratBet: betting && isBaccarat && baccaratRules ? { rules: baccaratRules } : null,    baccaratView: isBaccarat
      ? {
          playerCards: baccarat ? faceUpOnly(baccarat.playerCards) : [],
          bankerCards: baccarat ? faceUpOnly(baccarat.bankerCards) : [],
          playerTotal: baccarat?.playerTotal ?? null,
          bankerTotal: baccarat?.bankerTotal ?? null,
          outcomeLabel: baccarat ? baccaratOutcomeLabel(baccarat) : null,
          backedLabel: myBaccarat?.spot
            ? `You backed ${SPOT_LABELS[myBaccarat.spot]}${
                (mySeat?.bet ?? 0) > 0 ? ` for ${mySeat!.bet.toLocaleString()}` : ''
              }`
            : null,
          result: myBaccaratResult ? baccaratMainResult(myBaccaratResult, baccaratRules) : null,
          sideNotices: (myBaccarat?.sideBets ?? []).map((b) => {
            const n = baccaratSideBetNotice(b);
            return { key: b.id, tone: n.tone, text: n.text };
          }),
          placeholder: baccaratPlaceholder(phase, canDeal, hostName, betting),
          hasCards: (baccarat?.playerCards.length ?? 0) > 0,
        }
      : null,

    isRoulette,
    rouletteBet: (() => {
      if (!rouletteSeatLive || !rouletteRules) return null;
      const stacks = rouletteStacks(roulette, state?.seats ?? [], mySeatIndex);
      return {
        rules: rouletteRules,
        stacks,
        // Dolly and win flash wait for settle — the pocket is on the wire at release.
        pocket: rouletteSettled ? roulette?.pocket ?? null : null,
        marks: rouletteSettled
          ? rouletteWinMarks(roulette?.pocket ?? null, stacks)
          : {},
        canDrop: rouletteCanDrop,
      };
    })(),
    rouletteView,

    isKeno,
    kenoBet: kenoSeatLive && kenoRules ? { rules: kenoRules, canDrop: kenoCanDrop } : null,
    kenoView,

    isCraps,
    crapsBet:
      crapsSeatLive && crapsRules
        ? {
            rules: crapsRules,
            point: craps?.point ?? null,
            canDrop: crapsCanBet,
            showReady: phase === 'betting',
            rolling: phase === 'rolling',
            canRoll,
            shooterName: crapsView?.shooterName ?? null,
            stacks: crapsStacks(craps, state?.seats ?? [], mySeatIndex),
            marks: crapsMarks(craps),
            myBets: myCraps?.bets ?? [],
            hits:
              phase === 'rolling'
                ? (craps?.lastSettlements ?? [])
                    .filter(
                      (s) =>
                        s.seatIndex === mySeatIndex &&
                        s.resolution === 'win' &&
                        crapsCanPress(s.betId),
                    )
                    .map((s) => ({
                      betId: s.betId,
                      label: CRAPS_BETS[s.betId].label,
                      stake: s.amount,
                      payout: s.payout,
                      rides: s.rides,
                    }))
                : [],
          }
        : null,
    crapsView,

    isThreecard,
    threecardBet: betting && isThreecard && threecardRules ? { rules: threecardRules } : null,
    threecardView,

    tableView,
  };
}

const buildTableView = (
  state: RoomState,
  hand: PrivateHand | null,
  isHoldem: boolean,
  isBaccarat: boolean,
  mySeats: number[],
  phase: Phase,
): PlayTableViewVM => {
  const isRoulette = state.gameType === 'roulette';

  if (state.gameType === 'craps') {
    const craps = state.craps;
    return {
      holdem: false,
      streetLabel: null,
      boardCards: [],
      potLine: null,
      dealerLabel:
        craps?.lastRoll != null
          ? `${craps.lastRoll[0]} + ${craps.lastRoll[1]} = ${rollTotal(craps.lastRoll)}`
          : 'The dice',
      dealerCards: [],
      dealerValueLine: craps?.point == null ? 'Come out — puck OFF' : `Point ${craps.point}`,
      baccarat: null,
      craps: {
        rules: state.crapsRules,
        point: craps?.point ?? null,
        stacks: crapsStacks(craps ?? null, state.seats, mySeats[0] ?? null),
        marks: crapsMarks(craps ?? null),
        callLine: crapsRollCall(craps ?? null),
        shooterLine:
          craps?.shooterSeatIndex != null
            ? `${state.seats[craps.shooterSeatIndex]?.name ?? 'The shooter'} shooting · roll ${craps.rollNumber}`
            : null,
      },
      rows: state.seats
        .filter((s) => s.playerId !== null)
        .map((s) => {
          const cs = craps?.seats[s.index];
          const result = craps?.results?.find((r) => r.seatIndex === s.index) ?? null;
          const mine = mySeats.includes(s.index);
          const shooter = craps?.shooterSeatIndex === s.index;
          const betLines = (cs?.bets ?? []).map(
            (b) => `${CRAPS_BETS[b.id].label} ${b.amount}${b.working ? ' (working)' : ''}`,
          );
          return {
            index: s.index,
            name: s.name,
            nameLine: `Seat ${s.index + 1} — ${s.name}${mine ? ' (you)' : ''}${
              shooter ? ' · shooter' : ''
            }`,
            mine,
            acting: shooter && phase === 'rolling',
            chipsLine: `${s.name} has ${s.chips.toLocaleString()} chips`,
            cards: null,
            handLabel: result ? crapsRowResult(result).label : null,
            fallback:
              betLines.length > 0 ? betLines.join(' · ') : seatStatusLabel(s.status),
            committed: s.bet,
            hands: [],
          };
        }),
      nobodySeated: state.seats.every((s) => s.playerId === null),
    };
  }

  if (isRoulette) {
    const rou = state.roulette;
    return {
      holdem: false,
      streetLabel: null,
      boardCards: [],
      potLine: null,
      // Mid-roll the pocket is already known, so the plate waits for settle.
      dealerLabel:
        rou?.results != null && rou.pocket != null
          ? `Pocket ${pocketLabel(rou.pocket)}`
          : 'The wheel',
      dealerCards: [],
      dealerValueLine: null,
      baccarat: null,
      craps: null,
      rows: state.seats
        .filter((s) => s.playerId !== null)
        .map((s) => {
          const rs = rou?.seats[s.index];
          const result = rou?.results?.find((r) => r.seatIndex === s.index) ?? null;
          const mine = mySeats.includes(s.index);
          const stakeLines = (rs?.bets ?? []).map((b) => rouletteBetLabel(b));
          return {
            index: s.index,
            name: s.name,
            nameLine: `Seat ${s.index + 1} — ${s.name}${mine ? ' (you)' : ''}`,
            mine,
            acting: false,
            chipsLine: `${s.name} has ${s.chips.toLocaleString()} chips`,
            cards: null,
            handLabel: result
              ? result.payout === 0
                ? `Lost ${result.stake.toLocaleString()}`
                : formatSignedChips(result.payout - result.stake)
              : null,
            fallback:
              stakeLines.length > 0
                ? `${stakeLines.join(' · ')} — ${s.bet.toLocaleString()}`
                : seatStatusLabel(s.status),
            committed: s.bet,
            hands: [],
          };
        }),
      nobodySeated: state.seats.every((s) => s.playerId === null),
    };
  }

  if (state.gameType === 'keno') {
    const k = state.keno;
    return {
      holdem: false,
      streetLabel: null,
      boardCards: [],
      potLine: null,
      dealerLabel:
        k?.draw != null
          ? k.results != null
            ? `Draw ${k.drawNumber}`
            : 'Drawing…'
          : 'The board',
      dealerCards: [],
      dealerValueLine: null,
      baccarat: null,
      craps: null,
      rows: state.seats
        .filter((s) => s.playerId !== null)
        .map((s) => {
          const ks = k?.seats[s.index];
          const result = k?.results?.find((r) => r.seatIndex === s.index) ?? null;
          const mine = mySeats.includes(s.index);
          const ticketLines = (ks?.tickets ?? []).map((t) => describeKenoTicket(t.spots));
          return {
            index: s.index,
            name: s.name,
            nameLine: `Seat ${s.index + 1} — ${s.name}${mine ? ' (you)' : ''}`,
            mine,
            acting: false,
            chipsLine: `${s.name} has ${s.chips.toLocaleString()} chips`,
            cards: null,
            handLabel: result
              ? result.payout === 0
                ? `Lost ${result.stake.toLocaleString()}`
                : formatSignedChips(result.payout - result.stake)
              : null,
            fallback:
              ticketLines.length > 0
                ? `${ticketLines.length} ticket${ticketLines.length === 1 ? '' : 's'} — ${s.bet.toLocaleString()}`
                : seatStatusLabel(s.status),
            committed: s.bet,
            hands: [],
          };
        }),
      nobodySeated: state.seats.every((s) => s.playerId === null),
    };
  }

  if (state.gameType === 'threecard') {
    const tc = state.threecard;
    return {
      holdem: false,
      streetLabel: null,
      boardCards: [],
      potLine: null,
      dealerLabel:
        tc?.dealerLabel ?? (state.phase === 'betting' ? 'Betting open' : 'The dealer'),
      dealerCards: tc ? faceUpOnly(tc.dealerCards) : [],
      dealerValueLine:
        tc?.dealerQualifies != null
          ? tc.dealerQualifies
            ? 'Dealer qualifies'
            : 'Dealer doesn’t qualify'
          : tc && tc.dealerCards.length > 0
            ? `${tc.dealerCards.length} cards down`
            : null,
      baccarat: null,
      craps: null,
      rows: state.seats
        .filter((s) => s.playerId !== null)
        .map((s) => {
          const ts = tc?.seats[s.index];
          const result = tc?.results?.find((r) => r.seatIndex === s.index) ?? null;
          const mine = mySeats.includes(s.index);
          const myCards = mine
            ? hand?.threecard?.find((ps) => ps.seatIndex === s.index)?.cards ?? []
            : [];
          const revealed = result != null && result.outcome !== 'fold' && !ts?.folded;
          const shown = revealed ? faceUpOnly(ts?.cards ?? []) : myCards;
          const net = result ? result.payout - result.ante - result.play : 0;
          return {
            index: s.index,
            name: s.name,
            nameLine: `Seat ${s.index + 1} — ${s.name}${mine ? ' (you)' : ''}${
              state.activeSeatIndex === s.index ? ' — deciding' : ''
            }`,
            mine,
            acting: state.activeSeatIndex === s.index,
            chipsLine: `${s.name} has ${s.chips.toLocaleString()} chips`,
            cards: shown.length > 0 ? shown : null,
            handLabel:
              result && result.outcome !== 'fold'
                ? net === 0
                  ? 'Push'
                  : formatSignedChips(net)
                : result
                  ? `Folded ${formatSignedChips(result.payout - result.ante)}`
                  : null,
            fallback: ts?.folded
              ? 'Folded'
              : ts && ts.cards.length > 0
                ? `${ts.cards.length} cards down`
                : seatStatusLabel(s.status),
            committed: s.bet + (ts?.play ?? 0),
            hands: [],
          };
        }),
      nobodySeated: state.seats.every((s) => s.playerId === null),
    };
  }

  if (isBaccarat) {
    const bac = state.baccarat;
    return {
      holdem: false,
      streetLabel: null,
      boardCards: [],
      potLine: null,
      dealerLabel: '',
      dealerCards: [],
      dealerValueLine: null,
      baccarat: {
        playerCards: bac ? faceUpOnly(bac.playerCards) : [],
        bankerCards: bac ? faceUpOnly(bac.bankerCards) : [],
        playerTotal: bac?.playerTotal ?? null,
        bankerTotal: bac?.bankerTotal ?? null,
        playerLabel: 'Player',
        bankerLabel: 'Banker',
      },
      craps: null,
      rows: state.seats
        .filter((s) => s.playerId !== null)
        .map((s) => {
          const bs = bac?.seats[s.index];
          const result = bac?.results?.find((r) => r.seatIndex === s.index) ?? null;
          const mine = mySeats.includes(s.index);
          return {
            index: s.index,
            name: s.name,
            nameLine: `Seat ${s.index + 1} — ${s.name}${mine ? ' (you)' : ''}`,
            mine,
            acting: false,
            chipsLine: `${s.name} has ${s.chips.toLocaleString()} chips`,
            cards: null,
            handLabel: result ? baccaratRowResult(result) : null,
            fallback: bs?.spot
              ? `${SPOT_LABELS[bs.spot]} · ${s.bet.toLocaleString()}`
              : seatStatusLabel(s.status),
            committed: s.bet,
            hands: [],
          };
        }),
      nobodySeated: state.seats.every((s) => s.playerId === null),
    };
  }

  if (isHoldem && state.holdem) {
    const holdem = state.holdem;
    return {
      holdem: true,
      streetLabel: holdem.street ? STREET_LABELS[holdem.street] ?? 'Board' : 'Board',
      boardCards: faceUpOnly(holdem.board),
      potLine: `Pot ${holdem.potTotal.toLocaleString()}`,
      dealerLabel: '',
      dealerCards: [],
      dealerValueLine: null,
      baccarat: null,
      craps: null,
      rows: state.seats
        .filter((s) => s.playerId !== null)
        .map((s) => {
          const hs = holdem.seats[s.index];
          const mine = mySeats.includes(s.index);
          const acting = state.activeSeatIndex === s.index;
          const mineCards = mine
            ? hand?.holdem?.find((ps) => ps.seatIndex === s.index)?.cards ?? []
            : [];
          const shown =
            hs?.revealed && hs.cards.length > 0 ? faceUpOnly(hs.cards) : mineCards;
          return {
            index: s.index,
            name: s.name,
            nameLine: `Seat ${s.index + 1} — ${s.name}${mine ? ' (you)' : ''}${
              acting ? ' — acting now' : ''
            }`,
            mine,
            acting,
            chipsLine: `${s.name} has ${s.chips.toLocaleString()} chips`,
            cards: shown.length > 0 ? shown : null,
            handLabel: hs?.handLabel ?? null,
            fallback:
              hs?.folded
                ? 'Folded'
                : hs?.allIn
                  ? 'All in'
                  : hs && hs.cards.length > 0
                    ? `${hs.cards.length} cards down`
                    : seatStatusLabel(s.status),
            committed: hs && hs.committed > 0 ? hs.committed : 0,
            hands: [],
          };
        }),
      nobodySeated: state.seats.every((s) => s.playerId === null),
    };
  }

  return {
    holdem: false,
    streetLabel: null,
    boardCards: [],
    potLine: null,
    dealerLabel: `Dealer${state.dealer.revealed ? '' : ' — one card down'}`,
    dealerCards: faceUpOnly(state.dealer.cards),
    dealerValueLine:
      state.dealer.cards.length > 0
        ? `Shows ${state.dealer.visibleValue ?? '…'}${state.dealer.blackjack ? ' — blackjack' : ''}`
        : null,
    baccarat: null,
    craps: null,
    rows: state.seats
      .filter((s) => s.playerId !== null)
      .map((s) => {
        const mine = mySeats.includes(s.index);
        const acting = state.activeSeatIndex === s.index && phase === 'playing';
        // Live hands are face-down in the broadcast; this phone gets its
        // own seats' real cards separately, so splice those in.
        const privHands = mine
          ? hand?.seats.find((ps) => ps.seatIndex === s.index)?.hands ?? []
          : [];
        const showingPrivate = privHands.length > 0;
        const hands = showingPrivate ? privHands : s.hands;
        return {
          index: s.index,
          name: s.name,
          nameLine: `Seat ${s.index + 1} — ${s.name}${mine ? ' (you)' : ''}${
            acting ? ' — acting now' : ''
          }`,
          mine,
          acting,
          chipsLine: `${s.name} has ${s.chips.toLocaleString()} chips`,
          cards: null,
          handLabel: null,
          fallback:
            hands.length > 0
              ? null
              : `${seatStatusLabel(s.status)}${s.bet > 0 ? ` · bet ${s.bet}` : ''}`,
          committed: s.bet,
          hands: hands.map((h, i) => ({
            cards: showingPrivate
              ? (h as PrivateHandView).cards
              : faceUpOnly((h as PublicHand).cards),
            valueLabel: showingPrivate
              ? handValueLabel(h as PrivateHandView)
              : publicHandValueLabel(h as PublicHand),
            bet: h.bet,
            handTag: hands.length > 1 ? `hand ${i + 1}` : null,
            result: showingPrivate ? null : handResultLabel(h as PublicHand),
          })),
        };
      }),
    nobodySeated: state.seats.every((s) => s.playerId === null),
  };
};
